/* eslint-disable max-lines */

import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

type ComponentProps = {
  treeContext: React.Context<FiltersTreeContext>;

  dropdownDirection?: 'down' | 'up';
  loading?: boolean;
};

const GroupOptionsMenu: React.FC<ComponentProps> = (props) => {
  const { treeContext, dropdownDirection = 'down', loading = false } = props;

  const {
    selectedFields,
    selectedValues,
    handleAddValuesToGroup,
    handleRemoveValuesFromGroup,
    handleAddFieldsToGroup,
    handleRemoveFieldsFromGroup,
    handleClearSelectedFilters,
    handleClearSelectedFields,
  } = useContext(treeContext);

  const [isOpen, setIsOpen] = useState(false);
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  const handleAddToGroups = (): void => {
    if (selectedValues.length <= 1) return;

    handleAddValuesToGroup(selectedValues);
    setIsOpen(false);
  };

  const handleRemoveFromGroups = (): void => {
    handleRemoveValuesFromGroup(selectedValues);
    setIsOpen(false);
  };

  const handleGroupFields = (): void => {
    if (selectedFields.length <= 1) return;

    handleAddFieldsToGroup(selectedFields);
    setIsOpen(false);
  };

  const handleRemoveFieldsFromGroups = (): void => {
    handleRemoveFieldsFromGroup(selectedFields);
    setIsOpen(false);
  };

  const handleUnselect = (): void => {
    handleClearSelectedFilters();
    handleClearSelectedFields();
    setIsOpen(false);
  };

  const isOnGroup = useMemo(
    (): boolean => selectedValues.some((pill) => pill.group),
    [selectedValues]
  );

  const isFieldInGroup = useMemo(
    (): boolean => selectedFields.some((pill) => pill.group),
    [selectedFields]
  );

  const renderButton = (): JSX.Element => (
    <div
      className={`w-full h-6.5 flex flex-row items-center rounded ${
        selectedValues.length === 0 && selectedFields.length === 0
          ? 'bg-litlingo-gray-3'
          : 'bg-litlingo-primary'
      }`}
    >
      <button
        type="button"
        className="button button--primary rounded-r-none h-full flex gap-1 justify-center py-0.5 px-2 text-body w-full focus:outline-none"
        onClick={handleAddToGroups}
        disabled={selectedValues.length <= 1}
      >
        {loading ? (
          <LoadingIndicator size="5" />
        ) : (
          <span className="font-bold text-white whitespace-no-wrap">Group</span>
        )}
      </button>
      <span className="text-white text-center  w-2 h-6">&#x7C;</span>
      <button
        type="button"
        data-testid="button-menu-open"
        className="button button--primary h-full flex gap-1 justify-center py-0.5 px-2 text-body w-full border-0 focus:outline-none"
        onClick={(): void => setIsOpen(!isOpen)}
        disabled={selectedValues.length === 0 && selectedFields.length === 0}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.49026 8.44392L1.83398 3L0 4.91123L7.49035 12.0926L15 4.91123L13.166 3L7.49026 8.44392Z"
            fill="#ffffff"
          />
        </svg>
      </button>
    </div>
  );

  const renderMenu = (): JSX.Element | undefined => (
    <>
      <div
        data-testid="dropdown-menu"
        className={`origin-bottom-right right-0 absolute rounded z-50 border border-litlingo-gray-2 ${
          dropdownDirection === 'down' ? 'top-full mt-1' : 'top-0 -mt-1 transform -translate-y-full'
        }`}
      >
        <div
          className="rounded bg-white shadow-xs overflow-hidden"
          style={{ boxShadow: '3px 1px 8px rgba(0, 0, 0, 0.35)' }}
        >
          <div
            role="menu"
            data-testid="options-container"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            {selectedFields.length > 1 && (
              <button
                data-testid="menu-option-edit-review-set"
                type="button"
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onClick={handleGroupFields}
                className="text-body w-full border-litlingo-gray-2 p-2 pr-6 focus:outline-none hover:bg-litlingo-gray-1"
              >
                <div className="flex">
                  <span className="whitespace-no-wrap">Group Fields</span>
                </div>
              </button>
            )}

            {isFieldInGroup && (
              <button
                data-testid="menu-option-edit-review-set"
                type="button"
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onClick={handleRemoveFieldsFromGroups}
                className="text-body w-full border-litlingo-gray-2 p-2 pr-6 focus:outline-none hover:bg-litlingo-gray-1"
              >
                <div className="flex">
                  <span className="whitespace-no-wrap">Ungroup Fields</span>
                </div>
              </button>
            )}

            {isOnGroup && (
              <button
                data-testid="menu-option-edit-review-set"
                type="button"
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onClick={handleRemoveFromGroups}
                className="text-body w-full border-litlingo-gray-2 p-2 pr-6 focus:outline-none hover:bg-litlingo-gray-1"
              >
                <div className="flex">
                  <span className="whitespace-no-wrap">Ungroup</span>
                </div>
              </button>
            )}
            <button
              data-testid="menu-option-edit-review-set"
              type="button"
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick={handleUnselect}
              className="text-body w-full border-litlingo-gray-2 p-2 pr-6 focus:outline-none hover:bg-litlingo-gray-1"
            >
              <div className="flex">
                <span className="whitespace-no-wrap">Unselect All</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div ref={wrapperRef} className="relative w-full">
      {renderButton()}
      {isOpen && renderMenu()}
    </div>
  );
};

export default GroupOptionsMenu;
